import React, { lazy, Suspense, useEffect } from "react";
import AnnouncementBarV2 from "@/components/AnnouncementBarV2";
import PillNav from "@/components/PillNav";
import HeroV2 from "@/components/HeroV2";
import SleepBenefits from "@/components/SleepBenefits";
import PillowGallery from "@/components/PillowGallery";
import ProblemSection from "@/components/ProblemSection";
import SolutionSection from "@/components/SolutionSection";
import VideoTestimonialsGrid from "@/components/VideoTestimonialsGrid";
import ColorShowcase from "@/components/ColorShowcase";
import LovedWorldwide from "@/components/LovedWorldwide";
import ShippedTicker from "@/components/ShippedTicker";
import ThreeReasons from "@/components/ThreeReasons";
import Composition from "@/components/Composition";
import Craftsmanship from "@/components/Craftsmanship";
import DoctorApproved from "@/components/DoctorApproved";
import ClinicalRelief from "@/components/ClinicalRelief";
import Personas from "@/components/Personas";
import HowItWorksV2 from "@/components/HowItWorksV2";
import ShopZone from "@/components/ShopZone";
import SleepQuiz from "@/components/SleepQuiz";
import ExitPopup from "@/components/ExitPopup";
import LiveNotifications from "@/components/LiveNotifications";
import StickyMobileCta from "@/components/StickyMobileCta";
import NewsletterSlideIn from "@/components/NewsletterSlideIn";
import StickyDesktopCart from "@/components/StickyDesktopCart";
import FloatingCouponBanner from "@/components/FloatingCouponBanner";
import TrustReinforcer from "@/components/TrustReinforcer";
import OrderTracking from "@/components/OrderTracking";
import WorldsBestPillow from "@/components/WorldsBestPillow";
import RoyalsApproved from "@/components/RoyalsApproved";
import UsCompanyStrip from "@/components/UsCompanyStrip";
import Seo from "@/components/Seo";
import { mpTrack } from "@/lib/metaPixel";

const ComparisonLuxury = lazy(() => import("@/components/ComparisonLuxury"));
const NightsTimeline = lazy(() => import("@/components/NightsTimeline"));
const TestimonialsV2 = lazy(() => import("@/components/TestimonialsV2"));
const FaqV2 = lazy(() => import("@/components/FaqV2"));
const FinalCtaV2 = lazy(() => import("@/components/FinalCtaV2"));
const FooterV2 = lazy(() => import("@/components/FooterV2"));
const LuxuryGallery = lazy(() => import("@/components/LuxuryGallery"));

const Skel = () => (
  <div className="py-20 text-center text-[#8a8a8a] text-sm">Loading…</div>
);

/**
 * Conversion-optimized flow (Phase 1 - Feb 2026):
 *  1. Hero (hook + price + 5★ + CTAs) — 3-second sale
 *  2. SleepBenefits (quick trust bar)
 *  3. ProblemSection (pain trigger) — empathy first
 *  4. SolutionSection (reveal product)
 *  5. ComparisonLuxury (us vs ordinary pillow) — defeat objection #1
 *  6. VideoTestimonialsGrid (social proof)
 *  7. ⭐ ShopZone (sale point — most clicks happen here)
 *  8. TestimonialsV2 (written reviews)
 *  9. ThreeReasons + Composition + Craftsmanship + DoctorApproved (authority stack)
 * 10. Personas (identity bridge)
 * 11. NightsTimeline (future-pacing)
 * 12. PillowGallery + LuxuryGallery (visual recall)
 * 13. HowItWorksV2 (de-risk)
 * 14. SleepQuiz (engagement / lead capture)
 * 15. FAQ (last objections)
 * 16. FinalCtaV2 (last close)
 * 17. Footer
 */
export default function Landing() {
  useEffect(() => {
    // Fire Meta Pixel ViewContent once when the landing mounts.
    // Bundle price = $147 (1×), used as canonical product value.
    mpTrack("ViewContent", {
      content_ids: ["DP-CLASSIC"],
      content_name: "Dubai Pillow · Luxury Goose Down",
      content_type: "product",
      content_category: "Pillows",
      value: 147,
      currency: "USD",
    });
  }, []);

  return (
    <div className="bg-[#fafaf9]">
      <Seo />
      <AnnouncementBarV2 />
      <PillNav />
      <HeroV2 />
      <UsCompanyStrip />
      <ShippedTicker />
      <LovedWorldwide />
      <SleepBenefits />
      <ProblemSection />
      <SolutionSection />
      <WorldsBestPillow />
      <RoyalsApproved />
      <Suspense fallback={<Skel />}>
        <ComparisonLuxury />
      </Suspense>
      <VideoTestimonialsGrid />
      <ShopZone />
      <ColorShowcase />
      <Suspense fallback={<Skel />}>
        <TestimonialsV2 />
      </Suspense>
      <ThreeReasons />
      <ClinicalRelief />
      <Composition />
      <Craftsmanship />
      <DoctorApproved />
      <Personas />
      <Suspense fallback={<Skel />}>
        <NightsTimeline />
      </Suspense>
      <PillowGallery />
      <Suspense fallback={<Skel />}>
        <LuxuryGallery />
      </Suspense>
      <HowItWorksV2 />
      <div id="quiz">
        <SleepQuiz />
      </div>
      <Suspense fallback={<Skel />}>
        <FaqV2 />
      </Suspense>
      <TrustReinforcer />
      <OrderTracking />
      <Suspense fallback={<Skel />}>
        <FinalCtaV2 />
      </Suspense>
      <Suspense fallback={<Skel />}>
        <FooterV2 />
      </Suspense>

      <ExitPopup />
      <LiveNotifications />
      <NewsletterSlideIn />
      <StickyMobileCta />
      <StickyDesktopCart />
      <FloatingCouponBanner />
    </div>
  );
}
